import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Security } from '../../proto/generated/security'
import { Box, Divider, Grid, Paper } from '@mui/material'
import { getExchangeAndCodeFromKey, getSecurityAsync, getSecurityKey } from '../../util/security'
import { exchangeFromJSON } from '../../proto/generated/common'
import { fetchSecuritySnapshot, fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { selectOverviewSecurity } from '../../selectors/security'
import { selectOverviewOneDayDateRange } from '../../selectors/chart'
import { getOverviewWidget } from '../../services/apis/securities'
import SecurityOverviewSkeleton from './SecurityOverviewSkeleton'
import SubtleShimmer from '../common/SubtleShimmer'
import {
    OverviewWidgetResponse_FieldGroup,
    OverviewWidgetResponse_IndexComponent
} from '../../proto/generated/overview-widget'
import SecurityOverviewHeader from './SecurityOverviewHeader'
import OverviewChartSlot from './OverviewChartSlot'
import { overviewDateRangeKey } from './overviewChartProps'
import ScreenerOverviewDetails from './ScreenerOverviewDetails'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(() => ({
    scrollableDiv: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        borderRadius: '6px',
        overflowY: 'auto'
    },
    mainContainer: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderTopRightRadius: '6px'
    },
    stickyPaper: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        zIndex: 10
    },
    chartContainerWrap: {
        height: '100%',
        width: '100%'
    },
    chartSlot: {
        width: '100%',
        flexShrink: 0,
        overflow: 'hidden'
    },
    contentBox: {
        overflowY: 'auto'
    },
    fullWidthContainer: {
        width: '100%'
    }
}))

function ScreenerSecurityOverview() {
    const fieldGroups = useSelector((state: RootState) => state.overviewStore.fieldGroups)
    const indexComponents = useSelector((state: RootState) => state.overviewStore.indexComponents)
    const overviewSecurityKey = useSelector((state: RootState) => state.overviewStore.overviewSecurityKey)
    const screenerSecurity = useSelector(selectOverviewSecurity)
    const overviewDateRange = useSelector(selectOverviewOneDayDateRange, (prev, next) =>
        overviewDateRangeKey(prev) === overviewDateRangeKey(next)
    )
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const scrollRef = useRef(null)
    const { classes } = useStyles()

    const screenerContentSecurityRef = useRef<Security | undefined>(undefined)
    const screenerFieldGroupsRef = useRef<OverviewWidgetResponse_FieldGroup[]>([])
    const screenerIndexComponentsRef = useRef<OverviewWidgetResponse_IndexComponent[]>([])
    const [displayedSecurity, setDisplayedSecurity] = useState<Security | undefined>(undefined)
    const [initialLoadComplete, setInitialLoadComplete] = useState(false)

    const screenerTargetKey = useMemo(() => {
        if (!screenerSecurity || screenerSecurity === 'UNRECOGNIZED:undefined') return null
        return screenerSecurity
    }, [screenerSecurity])

    useEffect(() => {
        if (initialLoadComplete) return
        if (!loading && displayedSecurity) {
            setInitialLoadComplete(true)
        }
    }, [initialLoadComplete, loading, displayedSecurity])

    useEffect(() => {
        if (!screenerTargetKey) return
        let cancelled = false
        const isFirstScreenerLoad = !initialLoadComplete
        if (isFirstScreenerLoad) {
            setLoading(true)
        }
        ;(async () => {
            let security = await getSecurityAsync(screenerTargetKey)
            if (cancelled) return
            if (!security) {
                const [exc, code] = getExchangeAndCodeFromKey(screenerTargetKey)
                const response = await fetchSecuritySnapshot({
                    security: { exchange: exchangeFromJSON(exc), code: code },
                    dispatch
                })
                security = response.security
            }
            if (cancelled || !security) {
                if (isFirstScreenerLoad) {
                    setLoading(false)
                }
                return
            }
            setDisplayedSecurity(security)
            fetchSecuritySnapshotsBulk({
                securities: [{ exchange: security.exchange, code: security.code }],
                dispatch
            }).catch(() => undefined)
            try {
                await getOverviewWidget({
                    exchange: security.exchange,
                    code: security.code,
                    dispatch
                })
            } catch (error) {
                console.error('Error fetching screener overview data', error)
            }
            if (!cancelled) {
                setLoading(false)
                setInitialLoadComplete(true)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [screenerTargetKey, dispatch, initialLoadComplete])

    const overviewFieldGroups = useMemo(() => {
        if (screenerTargetKey && overviewSecurityKey === screenerTargetKey) {
            screenerFieldGroupsRef.current = fieldGroups
            return fieldGroups
        }
        return screenerFieldGroupsRef.current
    }, [fieldGroups, screenerTargetKey, overviewSecurityKey])

    const overviewIndexComponents = useMemo(() => {
        if (screenerTargetKey && overviewSecurityKey === screenerTargetKey) {
            screenerIndexComponentsRef.current = indexComponents
            return indexComponents
        }
        return screenerIndexComponentsRef.current
    }, [indexComponents, screenerTargetKey, overviewSecurityKey])

    const securityForContent = useMemo(() => {
        if (displayedSecurity && screenerTargetKey && overviewSecurityKey === screenerTargetKey) {
            screenerContentSecurityRef.current = displayedSecurity
            return displayedSecurity
        }
        return screenerContentSecurityRef.current
    }, [displayedSecurity, screenerTargetKey, overviewSecurityKey])

    const showOverviewChart = !!securityForContent
    const showOverviewHeader = displayedSecurity && (!loading || initialLoadComplete)

    const isScreenerContentUpdating = useMemo(
        () => initialLoadComplete && !!screenerTargetKey && overviewSecurityKey !== screenerTargetKey,
        [initialLoadComplete, screenerTargetKey, overviewSecurityKey]
    )

    const showShimmer = !initialLoadComplete && loading

    if (showShimmer) {
        return (
            <SubtleShimmer loading fill>
                <SecurityOverviewSkeleton />
            </SubtleShimmer>
        )
    }

    return (
        <Paper ref={scrollRef} id="scrollableDiv" className={classes.scrollableDiv}>
            {showOverviewHeader && displayedSecurity ? (
                <Box className={classes.mainContainer}>
                    <Paper className={classes.stickyPaper}>
                        <Grid container>
                            <SecurityOverviewHeader currentVisibleOverviewSecurity={displayedSecurity} />
                            <Divider className={classes.fullWidthContainer} />
                        </Grid>
                    </Paper>
                </Box>
            ) : null}
            <Box
                className={classes.contentBox}
                sx={{
                    opacity: isScreenerContentUpdating ? 0.72 : 1,
                    pointerEvents: isScreenerContentUpdating ? 'none' : 'auto'
                }}
            >
                <Box
                    className={classes.chartSlot}
                    sx={{
                        height: showOverviewChart ? 250 : 0,
                        opacity: showOverviewChart ? 1 : 0
                    }}
                >
                    {showOverviewChart && securityForContent && (
                        <OverviewChartSlot
                            security={securityForContent}
                            dateRange={overviewDateRange ?? undefined}
                        />
                    )}
                </Box>
                <ScreenerOverviewDetails
                    indexComponents={overviewIndexComponents}
                    fieldGroups={overviewFieldGroups}
                    securityForContent={securityForContent}
                    screenerTargetKey={screenerTargetKey}
                    overviewSecurityKey={overviewSecurityKey ?? undefined}
                />
            </Box>
        </Paper>
    )
}

export default memo(ScreenerSecurityOverview)
